import { OAUTH_CONFIG } from '../config/oauth.config';
import { generatePKCE, generateState } from './pkce';
import { authStore, TokenData } from './auth-store';

export interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in?: number;
  token_type: string;
}

const ALLOWED_ROLES = ['admin', 'superadmin'];

class AuthService {
  async initiateLogin(): Promise<void> {
    const { codeVerifier, codeChallenge } = await generatePKCE();
    const state = generateState();

    authStore.setCodeVerifier(codeVerifier);
    authStore.setState(state);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: OAUTH_CONFIG.CLIENT_ID,
      redirect_uri: OAUTH_CONFIG.REDIRECT_URI,
      scope: OAUTH_CONFIG.SCOPE,
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    if (OAUTH_CONFIG.AUDIENCE) {
      params.append('audience', OAUTH_CONFIG.AUDIENCE);
    }

    window.location.href = `${OAUTH_CONFIG.AUTHORIZATION_URL}?${params.toString()}`;
  }

  async handleCallback(code: string, state: string): Promise<boolean> {
    const storedState = authStore.getState();
    if (state !== storedState) {
      console.error('OAuth state mismatch');
      return false;
    }

    const codeVerifier = authStore.getCodeVerifier();
    if (!codeVerifier) {
      console.error('Code verifier not found');
      return false;
    }

    try {
      const response = await fetch(OAUTH_CONFIG.TOKEN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: OAUTH_CONFIG.CLIENT_ID,
          code,
          redirect_uri: OAUTH_CONFIG.REDIRECT_URI,
          code_verifier: codeVerifier,
        }),
      });

      if (!response.ok) {
        const error = await response.text();
        console.error('Token exchange failed:', error);
        return false;
      }

      const tokenResponse: TokenResponse = await response.json();
      const tokenData: TokenData = {
        accessToken: tokenResponse.access_token,
        refreshToken: tokenResponse.refresh_token,
        idToken: tokenResponse.id_token,
        expiresIn: tokenResponse.expires_in,
      };

      authStore.setTokens(tokenData);
      authStore.clearCodeVerifier();
      authStore.clearState();

      // Check if user has admin or superadmin role
      if (!this.hasAllowedRole()) {
        console.error('Access denied: User does not have admin or superadmin role');
        await this.logout();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Token exchange error:', error);
      return false;
    }
  }

  async refreshAccessToken(): Promise<boolean> {
    const refreshToken = authStore.getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await fetch(OAUTH_CONFIG.TOKEN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: OAUTH_CONFIG.CLIENT_ID,
          refresh_token: refreshToken,
        }),
      });

      if (!response.ok) {
        authStore.clearTokens();
        return false;
      }

      const tokenResponse: TokenResponse = await response.json();
      const tokenData: TokenData = {
        accessToken: tokenResponse.access_token,
        refreshToken: tokenResponse.refresh_token || refreshToken,
        expiresIn: tokenResponse.expires_in,
      };

      authStore.setTokens(tokenData);
      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      authStore.clearTokens();
      return false;
    }
  }

  async logout(): Promise<void> {
    const accessToken = authStore.getAccessToken();
    const refreshToken = authStore.getRefreshToken();
    const idToken = authStore.getIdToken();

    console.log('[AuthService] Starting logout process...');

    // 1. Attempt to revoke tokens and clear session on server via POST
    if (OAUTH_CONFIG.LOGOUT_URL) {
      try {
        console.log('[AuthService] Calling POST logout endpoint...');
        const response = await fetch(OAUTH_CONFIG.LOGOUT_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
          },
          body: refreshToken ? JSON.stringify({ refresh_token: refreshToken }) : null,
          credentials: 'include',
        });

        if (!response.ok) {
          console.warn('[AuthService] POST logout returned status:', response.status);
        } else {
          console.log('[AuthService] POST logout successful');
        }
      } catch (error) {
        console.error('[AuthService] POST logout failed (ignoring):', error);
      }
    }

    // 2. Clear local auth state
    authStore.clearTokens();

    // 3. Perform RP-Initiated Logout (OIDC redirect)
    // This is the most reliable way to clear session cookies on the auth server
    if (OAUTH_CONFIG.OIDC_LOGOUT_URL) {
      const state = generateState();
      const logoutUrl = new URL(OAUTH_CONFIG.OIDC_LOGOUT_URL);

      if (idToken) {
        logoutUrl.searchParams.append('id_token_hint', idToken);
      }

      // Use window.location.origin as a fallback for the redirect
      // Note: This must be registered as a valid post_logout_redirect_uri in the Auth Server
      logoutUrl.searchParams.append('post_logout_redirect_uri', window.location.origin);
      logoutUrl.searchParams.append('client_id', OAUTH_CONFIG.CLIENT_ID);
      logoutUrl.searchParams.append('state', state);

      console.log('[AuthService] Redirecting to OIDC provider logout:', logoutUrl.toString());

      // Small delay to allow the POST fetch to settle (optional but recommended)
      setTimeout(() => {
        window.location.href = logoutUrl.toString();
      }, 100);
      return;
    }

    // Fallback if no logout URL is configured
    console.log('[AuthService] No logout URL configured, redirecting to home...');
    setTimeout(() => {
      window.location.href = '/';
    }, 200);
  }

  hasAllowedRole(): boolean {
    const userInfo = authStore.getUserInfo();
    if (!userInfo) return false;

    // Check single role field
    if (userInfo.role && ALLOWED_ROLES.includes(userInfo.role.toLowerCase())) {
      return true;
    }

    // Check roles array
    if (userInfo.roles && Array.isArray(userInfo.roles)) {
      return userInfo.roles.some(role => ALLOWED_ROLES.includes(role.toLowerCase()));
    }

    return false;
  }

  isAuthenticated(): boolean {
    return authStore.isAuthenticated();
  }

  getAccessToken(): string | null {
    return authStore.getAccessToken();
  }
}

export const authService = new AuthService();
